import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, CheckCircle2, Lock, Landmark,
  Sparkles, TrendingUp, Clock, Info, X, Banknote,
} from "lucide-react";
import { Button } from "../../components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Optional Plaid bank-link "Boost your score" step
// Route: /preview/register-boost-score
// Public, non-destructive — renders the proposed post-registration screen for
// review. Does NOT call Plaid or backend. Two states viewable: idle | connected.
// ─────────────────────────────────────────────────────────────────────────────
export default function RegisterBoostScorePreview() {
  const [variant, setVariant] = useState("idle"); // idle | connected | placement

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">T</div>
            <span className="display font-semibold">TRAQO Preview</span>
          </Link>
          <div className="flex items-center gap-1 text-xs">
            {[
              ["idle", "Idle (offer)"],
              ["connected", "Connected (success)"],
              ["placement", "Placement in flow"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                data-testid={`preview-variant-${v}`}
                className={`px-3 py-1.5 rounded-full border transition-colors ${
                  variant === v
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/70 hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Context strip */}
        <div className="mb-8 rounded-xl border border-border bg-foreground/[0.02] p-5 flex items-start gap-3">
          <Info className="w-5 h-5 text-foreground/60 mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed text-foreground/75">
            <strong className="text-foreground">Proposed addition:</strong> after a buyer
            completes the existing 4-step wizard (You → Business → Identity → Categories)
            and the account is created, we briefly show this <em>optional</em> "Boost
            your score" screen <strong>before</strong> dropping them on{" "}
            <code className="text-xs bg-foreground/10 px-1.5 py-0.5 rounded">/buyer</code>.
            Connecting Plaid here pre-populates cashflow signals so the TRAQO Score is
            computed at first login instead of "—" until they wander into Payments.
            Sellers do <strong>not</strong> see this screen.
          </div>
        </div>

        {variant === "idle" && <IdleScreen />}
        {variant === "connected" && <ConnectedScreen />}
        {variant === "placement" && <PlacementMap />}

        {/* Recommendation panel */}
        <section className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border p-6">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              Where it lives
            </div>
            <h3 className="display text-xl font-semibold mb-3">
              Post-success interstitial
            </h3>
            <ul className="space-y-2 text-sm text-foreground/75 leading-relaxed">
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                Buyer is already authenticated (token issued by{" "}
                <code className="text-xs">/auth/register</code>) — Plaid Link works
                without re-auth.
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                Reuses the existing{" "}
                <code className="text-xs">{"<PlaidLinkButton />"}</code> + backend{" "}
                <code className="text-xs">/api/onboarding/plaid/*</code> endpoints. No new
                infra.
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                One file changed:{" "}
                <code className="text-xs">Register.jsx</code> — replace the final
                <code className="text-xs">{"nav(`/${u.role}`)"}</code> with{" "}
                <code className="text-xs">setStep(5)</code> for buyers.
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                "Skip" CTA always present — Plaid is a soft-uplift, never a blocker.
              </li>
            </ul>
          </div>
          <div className="rounded-xl border border-border p-6 bg-[#e8ff3a]/[0.05]">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              Why it lifts scores
            </div>
            <h3 className="display text-xl font-semibold mb-3">
              Cashflow ≫ self-reported revenue
            </h3>
            <ul className="space-y-2 text-sm text-foreground/75 leading-relaxed">
              <li className="flex gap-2">
                <TrendingUp className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                <span>
                  Plaid surfaces 24mo of <strong>real</strong> bank deposits, NSF count,
                  and avg balance — all primary inputs to the underwriting score.
                </span>
              </li>
              <li className="flex gap-2">
                <TrendingUp className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                <span>
                  Today, brand-new buyers default to a <strong>thin-file</strong> score
                  (~520-580). Connecting Plaid typically jumps healthy accounts to
                  680-740 the moment cashflow is parsed.
                </span>
              </li>
              <li className="flex gap-2">
                <TrendingUp className="w-4 h-4 text-foreground/60 mt-0.5 shrink-0" />
                <span>
                  Higher score → larger Net-30/60/90 limit at first checkout → better
                  GMV-conversion for sellers on the platform.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Edge cases */}
        <section className="mt-8 rounded-xl border border-dashed border-border p-6 bg-foreground/[0.02]">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
            Edge cases handled
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-foreground/75">
            <div>
              <div className="font-medium text-foreground mb-1">If Plaid fails</div>
              Toast error, button stays clickable, Skip remains.
            </div>
            <div>
              <div className="font-medium text-foreground mb-1">If buyer skips</div>
              Land on <code className="text-xs">/buyer</code>; dashboard already shows a
              "Connect bank to compute Score" tile. Nothing wasted.
            </div>
            <div>
              <div className="font-medium text-foreground mb-1">Invite-flow buyers</div>
              When <code className="text-xs">?invite=&lt;token&gt;</code> is present, we
              skip the boost screen and go straight to the supplier's catalog as today.
            </div>
          </div>
        </section>

        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Link to="/" className="text-sm text-foreground/60 hover:text-foreground">
            ← Back to home
          </Link>
          <span className="text-xs text-foreground/50">
            Preview only · no data is sent to Plaid or the backend
          </span>
        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State 1 — idle (the offer)
// ─────────────────────────────────────────────────────────────────────────────
function IdleScreen() {
  return (
    <BoostShell stepLabel="Step 5 of 5 · Optional">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/50 mb-3">
        <Sparkles className="w-3.5 h-3.5" />
        Boost your score
      </div>
      <h1 className="display text-3xl sm:text-4xl font-semibold leading-tight">
        Add 90+ points to your TRAQO Score in 30 seconds.
      </h1>
      <p className="mt-3 text-foreground/70 leading-relaxed">
        Connect your business bank read-only. We pull 24 months of cashflow to
        underwrite a real Net-30/60/90 limit at your first checkout. No statements,
        no PDFs, no waiting.
      </p>

      {/* Score visual */}
      <div className="mt-7 rounded-xl border border-border bg-background p-5">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-foreground/50 mb-3">
          <span>Your starting score</span>
          <span>With Plaid connected</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1 rounded-lg border border-border p-4">
            <div className="text-3xl display font-semibold text-foreground/40">—</div>
            <div className="text-xs text-foreground/50 mt-1">Thin file</div>
          </div>
          <ArrowRight className="w-5 h-5 text-foreground/40" />
          <div className="flex-1 rounded-lg border-2 border-foreground p-4 bg-[#e8ff3a]/10">
            <div className="text-3xl display font-semibold">724</div>
            <div className="text-xs text-foreground/70 mt-1">
              <TrendingUp className="w-3 h-3 inline mr-1" />
              Average for connected buyers
            </div>
          </div>
        </div>
      </div>

      {/* What we read */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          { icon: Banknote, t: "Avg deposits", s: "24-month average inflow" },
          { icon: TrendingUp, t: "Cashflow trend", s: "Growth or decline rate" },
          { icon: ShieldCheck, t: "NSF count", s: "Bounced-payment history" },
        ].map((c, i) => (
          <div key={i} className="rounded-lg border border-border p-3">
            <c.icon className="w-4 h-4 text-foreground/60 mb-1.5" />
            <div className="text-xs font-medium">{c.t}</div>
            <div className="text-[11px] text-foreground/55 leading-snug">{c.s}</div>
          </div>
        ))}
      </div>

      {/* Privacy strip */}
      <div className="mt-5 flex items-center gap-2 text-xs text-foreground/55 bg-foreground/[0.04] rounded-md px-3 py-2.5">
        <Lock className="w-3.5 h-3.5 shrink-0" />
        Read-only access · powered by Plaid · we can't move money or see passwords ·
        revoke anytime in Settings.
      </div>

      {/* CTAs */}
      <div className="mt-7 flex flex-col-reverse sm:flex-row gap-3">
        <Button
          variant="ghost"
          className="rounded-full h-12 px-6 text-foreground/70 hover:text-foreground"
          data-testid="preview-skip-plaid-btn"
        >
          Skip — I'll do this later
        </Button>
        <Button
          className="rounded-full h-12 px-6 bg-foreground text-background hover:bg-foreground/90 flex-1"
          data-testid="preview-connect-plaid-btn"
        >
          <Landmark className="w-4 h-4 mr-2" />
          Connect bank · Boost my score
        </Button>
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-foreground/45">
        <Clock className="w-3 h-3" />
        Average flow: 28 seconds
      </div>
    </BoostShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State 2 — connected (the success)
// ─────────────────────────────────────────────────────────────────────────────
function ConnectedScreen() {
  return (
    <BoostShell stepLabel="Step 5 of 5 · Connected">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0d8f4a] mb-3">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Bank connected
      </div>
      <h1 className="display text-3xl sm:text-4xl font-semibold leading-tight">
        Chase Business Checking linked. Score updated.
      </h1>
      <p className="mt-3 text-foreground/70 leading-relaxed">
        We read 24 months of cashflow in 6 seconds. Your underwriting profile is now
        live — checkout terms will reflect the new score immediately.
      </p>

      <div className="mt-7 rounded-xl border-2 border-foreground bg-[#e8ff3a]/10 p-6">
        <div className="text-xs uppercase tracking-widest text-foreground/60 mb-2">
          Your TRAQO Score
        </div>
        <div className="flex items-end gap-3">
          <div className="text-6xl display font-semibold leading-none">724</div>
          <div className="pb-1.5 text-sm text-[#0d8f4a] flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            +204 from baseline
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-foreground/70">
          <div className="flex justify-between border-t border-foreground/10 pt-2">
            <span>Approved Net-30 limit</span>
            <span className="font-medium text-foreground">$48,000</span>
          </div>
          <div className="flex justify-between border-t border-foreground/10 pt-2">
            <span>Net-60 / Net-90 eligible</span>
            <span className="font-medium text-foreground">Yes</span>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-border p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-foreground/5 grid place-items-center">
          <Landmark className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Chase Business Checking · ••4421</div>
          <div className="text-xs text-foreground/55">
            Linked just now · ACH-ready
          </div>
        </div>
        <button
          className="text-xs text-foreground/55 hover:text-foreground flex items-center gap-1"
          data-testid="preview-disconnect-plaid-btn"
        >
          <X className="w-3 h-3" />
          Disconnect
        </button>
      </div>

      <div className="mt-7">
        <Button
          className="w-full rounded-full h-12 bg-foreground text-background hover:bg-foreground/90"
          data-testid="preview-continue-to-dashboard-btn"
        >
          Continue to dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </BoostShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State 3 — placement diagram
// ─────────────────────────────────────────────────────────────────────────────
function PlacementMap() {
  const steps = [
    { n: "1", t: "You", s: "Name · email · phone · password", existing: true },
    { n: "2", t: "Business", s: "Legal name · EIN · address · type", existing: true },
    { n: "3", t: "Identity", s: "Owner KYC · DOB · SSN-last-4", existing: true },
    { n: "4", t: "Categories", s: "What you sell · soft-check consent", existing: true },
    {
      n: "5",
      t: "Boost score",
      s: "Plaid bank link · OPTIONAL · skippable",
      existing: false,
    },
  ];
  return (
    <div className="rounded-2xl border border-border bg-background p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
        Buyer signup flow
      </div>
      <h2 className="display text-2xl font-semibold mb-8">
        New step 5 sits after the existing 4 — never blocks completion
      </h2>
      <ol className="grid md:grid-cols-5 gap-3">
        {steps.map((s, i) => (
          <li
            key={s.n}
            className={`relative rounded-xl border p-4 ${
              s.existing
                ? "border-border bg-foreground/[0.02]"
                : "border-foreground bg-[#e8ff3a]/15"
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold mb-2 ${
                s.existing
                  ? "bg-foreground/10 text-foreground/70"
                  : "bg-foreground text-background"
              }`}
            >
              {s.n}
            </div>
            <div className="text-sm font-medium">{s.t}</div>
            <div className="text-[11px] text-foreground/60 mt-1 leading-snug">
              {s.s}
            </div>
            {!s.existing && (
              <div className="absolute -top-2 -right-2 text-[10px] uppercase tracking-wider bg-foreground text-background rounded-full px-2 py-0.5 font-semibold">
                NEW
              </div>
            )}
            {i < steps.length - 1 && (
              <div className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 text-foreground/30">
                →
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4 text-sm">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-1.5">
            Implementation
          </div>
          <div className="text-foreground/75 leading-relaxed">
            Add a 5th step branch in{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              Register.jsx
            </code>
            . On step-4 success, instead of{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              nav("/buyer")
            </code>
            , advance to step 5 (buyers only). Step 5 renders this screen using the
            existing{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              {"<PlaidLinkButton />"}
            </code>
            .
          </div>
        </div>
        <div className="rounded-lg border border-border p-4 text-sm">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-1.5">
            Backend
          </div>
          <div className="text-foreground/75 leading-relaxed">
            <strong>Zero changes.</strong> Endpoints{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              /api/onboarding/plaid/link-token
            </code>{" "}
            and{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              /exchange
            </code>{" "}
            already exist and require auth — buyer is logged in by step 5, so it just
            works.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell — mimics the right-side wizard panel from /register
// ─────────────────────────────────────────────────────────────────────────────
function BoostShell({ stepLabel, children }) {
  return (
    <div className="grid lg:grid-cols-[1fr_440px] gap-6 items-start">
      {/* Wizard panel */}
      <div className="rounded-2xl border border-border bg-background p-6 sm:p-10 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="text-xs uppercase tracking-widest text-foreground/50">
            {stepLabel}
          </div>
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <div
                key={n}
                className={`h-1 rounded-full transition-all ${
                  n < 5 ? "w-6 bg-foreground" : "w-12 bg-foreground"
                }`}
              />
            ))}
          </div>
        </div>
        {children}
      </div>

      {/* Side annotations */}
      <aside className="rounded-2xl border border-border bg-foreground/[0.02] p-6 sticky top-24 hidden lg:block">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          Annotations
        </div>
        <h3 className="display text-lg font-semibold mb-4">
          What you're looking at
        </h3>
        <ul className="space-y-3 text-sm text-foreground/70">
          <li className="flex gap-2">
            <span className="text-foreground font-semibold">①</span>
            Soft sell · "Skip" is always offered, never gated.
          </li>
          <li className="flex gap-2">
            <span className="text-foreground font-semibold">②</span>
            Score visual makes the value concrete — no "trust me" copy.
          </li>
          <li className="flex gap-2">
            <span className="text-foreground font-semibold">③</span>
            Privacy line addresses the #1 buyer hesitation upfront.
          </li>
          <li className="flex gap-2">
            <span className="text-foreground font-semibold">④</span>
            Average flow time published so buyers know the cost.
          </li>
        </ul>
      </aside>
    </div>
  );
}
